import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { StatusBadge } from '../../statusBadge/StatusBadge'

import { getDate } from '@percent/admin-dashboard/common/utility/getDate'
import { BankDetailsTableProps } from '@percent/admin-dashboard/app/bankDetails/bankDetailsContainer/bankDetailsTable/BankDetailsTable.types'
import {
  Copy,
  FlagAvatar,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableColumnSortDirection,
  TablePagination,
  TableProvider,
  TableRow
} from '@percent/lemonade'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'
import { LocaleKey } from '@percent/admin-dashboard/i18n/config'
import { BankDetailsFilters } from './bankDetailsFilters/BankDetailsFilters'

export function BankDetailsTable({
  data,
  isLoading,
  nextPage,
  previousPage,
  totalResults,
  errorMessage,
  queryParams,
  setQueryParams,
  hidePagination = false
}: BankDetailsTableProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const columns = useMemo(
    () => [
      { id: 'country', isSortable: false, props: { width: '80px' }, content: t(`table.country` as LocaleKey) },
      {
        id: 'organization',
        isSortable: false,
        props: { width: '344px' },
        content: t(`table.organization` as LocaleKey)
      },
      { id: 'reference', isSortable: false, props: { width: '120px' }, content: t(`table.reference` as LocaleKey) },
      {
        id: 'contactMail',
        isSortable: false,
        props: { width: '240px' },
        content: t(`table.contactMail` as LocaleKey)
      },
      { id: 'status', isSortable: false, props: { width: '200px' }, content: t(`table.status` as LocaleKey) },
      {
        id: 'requestDate',
        isSortable: true,
        props: { width: '200px' },
        content: t(`table.requestDate` as LocaleKey)
      },
      { id: 'actions', isSortable: false, props: { width: '40px' }, content: ' ' }
    ],
    [t]
  )

  const handleRequestSort = (_columnKey: string) => {
    const newDirection = queryParams.direction === 'ASC' ? 'DESC' : 'ASC'
    setQueryParams({ ...queryParams, direction: newDirection })
  }

  const displayTotalResults = errorMessage ? 0 : totalResults ?? 0

  return (
    <TableProvider
      columns={columns}
      totalResults={displayTotalResults}
      isLoading={isLoading}
      illustration="no-results"
      emptyTableTitle={t('table.bankDetailsEmptyTable')}
      emptyTableDescription=""
      dataTestId="bankDetailsTable"
      sortByColumn="requestDate"
      sortDirection={(queryParams.direction?.toUpperCase() as TableColumnSortDirection) ?? 'DESC'}
      handleRequestSort={handleRequestSort}
    >
      <BankDetailsFilters queryParams={queryParams} setQueryParams={setQueryParams} />

      <Table>
        <TableBody>
          {data?.map(row => (
            <TableRow key={row.organisationId} onClick={handleClickAsLink(`/bank-details-reviews/${row.id}`, navigate)}>
              <TableCell>
                <FlagAvatar code3={row.countryCode} />
              </TableCell>
              <TableCell>{row.organisationName}</TableCell>
              <TableCell
                onClick={e => {
                  e.stopPropagation()
                }}
              >
                <Copy text={row.reference}>{row.reference}</Copy>
              </TableCell>
              <TableCell>{row.email}</TableCell>
              <TableCell>
                <StatusBadge status={row.status} />
              </TableCell>
              <TableCell>{getDate(row.requestDate)}</TableCell>
              <TableCell>
                <Icon name="chevron-right" size={4} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!hidePagination && (
        <TablePagination
          totalResultsText={t('table.results', { count: displayTotalResults })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      )}
    </TableProvider>
  )
}
